import type { TFunction } from 'i18next'
import { useCallback, useEffect, useState, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { offersAPI } from '../api/client'

export interface OfferConversation {
  offer_id: number
  listing_id: number
  listing_title?: string | null
  offered_price: number
  role: 'buyer' | 'seller'
  is_accepted: boolean
  is_rejected: boolean
  offer_created_at: string
  last_message_body?: string | null
  last_message_at?: string | null
  message_count: number
}

interface OfferChatsPageProps {
  token: string
  onOpenConversation: (offerId: number, role: 'buyer' | 'seller') => void
}

function statusLabel(conv: OfferConversation, t: TFunction): string | null {
  if (conv.is_accepted) return t('seller.accepted')
  if (conv.is_rejected) return t('seller.passed')
  return t('offerStatus.pending')
}

export default function OfferChatsPage({ token, onOpenConversation }: OfferChatsPageProps) {
  const { t } = useTranslation()
  const [items, setItems] = useState<OfferConversation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(() => {
    setLoading(true)
    setError('')
    offersAPI
      .listOfferConversations(token)
      .then((res) => setItems(Array.isArray(res.data) ? (res.data as OfferConversation[]) : []))
      .catch((err) => {
        setItems([])
        setError(err.response?.data?.detail || t('offerChats.loadFailed'))
      })
      .finally(() => setLoading(false))
  }, [token, t])

  useEffect(() => {
    load()
  }, [load])

  return (
    <section className="offer-chats-page">
      <div className="offer-chats-page__hero">
        <div className="offer-chats-page__glow" aria-hidden="true" />
        <h2>{t('offerChats.title')}</h2>
        <p className="offer-chats-page__lead">{t('offerChats.lead')}</p>
      </div>

      {loading && <p className="meta-text offer-chats-page__status">{t('common.loading')}</p>}

      {!loading && error && (
        <div className="offer-chats-page__empty">
          <p className="error-text">{error}</p>
          <button type="button" className="catalog-empty-clear" onClick={load}>
            {t('catalog.refresh')}
          </button>
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="offer-chats-page__empty">
          <p>{t('offerChats.empty')}</p>
          <p className="offer-chats-page__empty-hint">{t('offerChats.emptyHint')}</p>
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <ul className="offer-chats-list">
          {items.map((conv, i) => {
            const when = conv.last_message_at || conv.offer_created_at
            const preview =
              conv.last_message_body?.trim() ||
              t('offerChats.noMessagesYet', { price: conv.offered_price.toFixed(2) })
            const status = statusLabel(conv, t)

            return (
              <li key={conv.offer_id} style={{ '--chat-i': i } as CSSProperties}>
                <button
                  type="button"
                  className="offer-chats-row"
                  onClick={() => onOpenConversation(conv.offer_id, conv.role)}
                >
                  <span className="offer-chats-row__icon" aria-hidden="true">
                    {conv.role === 'seller' ? '↩' : '→'}
                  </span>
                  <span className="offer-chats-row__body">
                    <span className="offer-chats-row__top">
                      <strong className="offer-chats-row__title">
                        {conv.listing_title || `${t('deals.listing')} #${conv.listing_id}`}
                      </strong>
                      <time className="offer-chats-row__time" dateTime={when}>
                        {new Date(when).toLocaleString()}
                      </time>
                    </span>
                    <span className="offer-chats-row__preview">{preview}</span>
                    <span className="offer-chats-row__meta">
                      <span className="offer-chats-row__role">
                        {conv.role === 'seller' ? t('offerChats.asSeller') : t('offerChats.asBuyer')}
                      </span>
                      {status ? <span className="offer-chats-row__status">{status}</span> : null}
                      {conv.message_count > 0 ? (
                        <span className="offer-chats-row__count">{conv.message_count}</span>
                      ) : null}
                    </span>
                  </span>
                  <span className="offer-chats-row__chev" aria-hidden="true">›</span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
